const SCORE_WEIGHTS = {
  businessStage: 20,
  productsReady: 30,
  marketingPlan: 20,
  targetLaunchDate: 15,
  subscriptionUnderstood: 15,
};

const normalize = (value) => String(value || "").trim().toLowerCase();

const findAnswer = (answers, questions, patterns) => {
  for (const [id, answer] of Object.entries(answers || {})) {
    const question = questions.find((item) => item?.id === id);
    const text = `${id} ${question?.label || ""}`.toLowerCase();
    if (patterns.some((pattern) => pattern.test(text)) && answer !== "" && answer != null) {
      return { id, answer, label: question?.label || id };
    }
  }
  return null;
};

const answerText = (answer) =>
  (Array.isArray(answer) ? answer : [answer])
    .map((item) => normalize(item))
    .filter(Boolean)
    .join(" ");

const scoreFor = (answer, category) => {
  const value = answerText(answer);
  if (!value) return null;

  if (category === "businessStage") {
    if (/idea|planning/.test(value)) return 25;
    if (/new|establish/.test(value)) return 60;
    if (/expand|operat|already|selling/.test(value)) return 100;
  }

  if (category === "productsReady") {
    if (/not ready|no.*ready|not.*listed/.test(value)) return 0;
    if (/partial|some.*ready|in progress/.test(value)) return 50;
    if (/ready|complete|yes/.test(value)) return 100;
  }

  if (category === "marketingPlan") {
    if (/need help|not sure|no strategy|none|haven't/.test(value)) return 35;
    if (/mix|both|organic and paid/.test(value)) return 100;
    if (/social|word of mouth|organic|paid|advertis/.test(value)) return 75;
  }

  if (category === "targetLaunchDate") {
    const date = new Date(answer);
    if (Number.isNaN(date.getTime())) return null;
    const daysUntilLaunch = Math.ceil(
      (date.setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) /
        (24 * 60 * 60 * 1000),
    );
    if (daysUntilLaunch < 0) return 0;
    if (daysUntilLaunch <= 30) return 100;
    if (daysUntilLaunch <= 90) return 80;
    return 60;
  }

  if (category === "subscriptionUnderstood") {
    if (/clarif|explain|not sure|no|unclear/.test(value)) return 30;
    if (/yes|clear|understand/.test(value)) return 100;
  }

  return null;
};

const CATEGORY_PATTERNS = {
  businessStage: [/businessstage/, /business.*stage/, /where.*business.*today/],
  productsReady: [/productsready/, /product.*(ready|listing)/, /(ready|list).*products?/],
  marketingPlan: [/marketingplan/, /marketing/, /attract.*customers?/],
  targetLaunchDate: [/targetlaunchdate/, /launch.*date/, /when.*launch/],
  subscriptionUnderstood: [
    /subscriptionunderstood/,
    /subscription/,
    /renewal.*cancellation/,
    /plan.*cost/,
  ],
};

export const analyzeBusinessAssessment = (answers, questions = []) => {
  if (!answers || typeof answers !== "object") return null;

  const categories = {};
  const scored = [];
  for (const [category, patterns] of Object.entries(CATEGORY_PATTERNS)) {
    const entry = findAnswer(answers, questions, patterns);
    const score = entry ? scoreFor(entry.answer, category) : null;
    categories[category] = entry ? { ...entry, score } : null;
    if (score !== null) {
      scored.push({ weight: SCORE_WEIGHTS[category], score });
    }
  }

  if (scored.length === 0) {
    return {
      score: null,
      level: "Not enough data",
      coverage: 0,
      supportLevel: null,
      supportFactors: [],
      recommendations: [],
      categories,
    };
  }

  const availableWeight = scored.reduce((total, item) => total + item.weight, 0);
  const score = Math.round(
    scored.reduce((total, item) => total + item.score * item.weight, 0) /
      availableWeight,
  );
  const coverage = Math.round(
    (availableWeight /
      Object.values(SCORE_WEIGHTS).reduce((total, weight) => total + weight, 0)) *
      100,
  );
  const level =
    scored.length < 2
      ? "Not enough data"
      : score >= 75
        ? "Ready to launch"
        : score >= 45
          ? "Preparing"
          : "Planning";

  const supportEntry = findAnswer(answers, questions, [/supportareas/, /support/, /help.*most/]);
  const experienceEntry = findAnswer(answers, questions, [/onlinesellingexperience/, /selling.*experience/, /sold online/]);
  const supportFactors = [];
  if (supportEntry && answerText(supportEntry.answer) && !/none|no help|self-service/.test(answerText(supportEntry.answer))) {
    supportFactors.push("Requested assistance");
  }
  if (experienceEntry && /first time|never|no/.test(answerText(experienceEntry.answer))) {
    supportFactors.push("New to selling online");
  }
  if (categories.subscriptionUnderstood?.score < 100) {
    supportFactors.push("Subscription terms need clarification");
  }
  if (categories.productsReady?.score < 100) {
    supportFactors.push("Product listings need preparation");
  }
  const supportLevel =
    supportFactors.length >= 3
      ? "High-touch guidance"
      : supportFactors.length > 0
        ? "Guided support"
        : supportEntry || experienceEntry
          ? "Self-service likely"
          : null;

  const recommendations = [];
  if (
    categories.businessStage?.score != null &&
    categories.businessStage.score <= 25
  ) {
    recommendations.push("Agree on a realistic launch milestone and readiness checklist.");
  }
  if (
    categories.productsReady?.score != null &&
    categories.productsReady.score < 100
  ) {
    recommendations.push("Help prepare product names, photos, descriptions and prices.");
  }
  if (categories.marketingPlan?.score === 35) {
    recommendations.push("Create a simple first-month customer acquisition plan.");
  }
  if (categories.subscriptionUnderstood?.score === 30) {
    recommendations.push("Clarify the plan cost, renewal date and cancellation terms before activation.");
  }
  if (supportEntry && supportFactors.includes("Requested assistance")) {
    recommendations.push("Follow up on the support areas selected by the client.");
  }
  if (recommendations.length === 0 && level === "Ready to launch") {
    recommendations.push("Confirm the launch date and agree on the first success milestone.");
  }

  return {
    score,
    level,
    coverage,
    supportLevel,
    supportFactors,
    recommendations,
    categories,
  };
};
